import { ListItemButton, ListSubheader } from '@mui/material';
import List from '@mui/material/List';
import Grid from '@mui/material/Unstable_Grid2';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import GroupOutlined from '@mui/icons-material/GroupOutlined';
import SecurityOutlined from '@mui/icons-material/SecurityOutlined';
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import DevicesOutlined from '@mui/icons-material/DevicesOutlined';
import IntegrationInstructionsOutlined from '@mui/icons-material/IntegrationInstructionsOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import ExtensionOutlined from '@mui/icons-material/ExtensionOutlined';
import CreditCardOutlined from '@mui/icons-material/CreditCardOutlined';
import DeleteForeverOutlined from '@mui/icons-material/DeleteForeverOutlined';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import { fetchSurveys } from 'client/modules/Surveys/SurveyActions';
import { getSurveys } from 'client/modules/Surveys/SurveyReducer';
import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import Sticky from 'react-stickynode';
import Layout from '../../../App/components/Layout/LayoutContainer';
import { getCurrentOrgId, hasFeature } from '../../../Auth/AuthReducer';
import { fetchSettings } from '../../SettingsActions';
import { isSettingsLoaded } from '../../SettingsReducer';
import styles from './SettingsPage.css';

const messages = defineMessages({
  // Organization section
  organization: {
    id: 'Settings.Organization',
    defaultMessage: 'Organization',
  },
  generalSettings: {
    id: 'Settings.GeneralSettings',
    defaultMessage: 'General',
  },
  teamSettings: {
    id: 'Settings.TeamSettings',
    defaultMessage: 'Team',
  },
  signInSecurity: {
    id: 'Settings.SignInSecurity',
    defaultMessage: 'Sign-in Security',
  },

  // Integrations section
  integrations: {
    id: 'Settings.Integrations',
    defaultMessage: 'Integrations',
  },
  apiKeys: {
    id: 'Settings.APIKeys',
    defaultMessage: 'API Keys',
  },
  emailSettings: {
    id: 'Settings.EmailSettings',
    defaultMessage: 'Email Settings',
  },
  integrationsSettings: {
    id: 'Settings.IntegrationsSettings',
    defaultMessage: 'Integrations',
  },

  // Personal section
  personal: {
    id: 'Settings.Personal',
    defaultMessage: 'Personal',
  },
  profileSettings: {
    id: 'Settings.ProfileSettings',
    defaultMessage: 'Account Security',
  },
  notifications: {
    id: 'Settings.Notifications',
    defaultMessage: 'Notifications',
  },
  sessionsSettings: {
    id: 'Settings.SessionsSettings',
    defaultMessage: 'Active Sessions',
  },
  billingSettings: {
    id: 'Settings.Billing',
    defaultMessage: 'Billing',
  },

  // Compliance section
  compliance: {
    id: 'Settings.Compliance',
    defaultMessage: 'Compliance',
  },
  dataManagement: {
    id: 'Settings.DataManagement',
    defaultMessage: 'Data Management',
  },
  auditLog: {
    id: 'Settings.AuditLog',
    defaultMessage: 'Audit Log',
  },

  back: {
    id: 'Settings.Back',
    defaultMessage: 'Back',
  },
  title: {
    id: 'Settings.Settings',
    defaultMessage: 'Settings',
  },
});

function SettingsPage() {
  const { page } = useParams();
  const { formatMessage } = useIntl();
  const { pathname } = useLocation();
  const orgId = useSelector(getCurrentOrgId);
  const dispatch = useDispatch();
  const features = useSelector(state => ({
    sso: hasFeature(state, 'sso'),
    api: hasFeature(state, 'api'),
    integrations: hasFeature(state, 'integrations'),
    auditLog: hasFeature(state, 'auditLog'),
  }));

  useEffect(() => {
    if (orgId) {
      dispatch(fetchSettings());
    }
  }, [dispatch, orgId]);

  const levels = pathname.split('/').filter(Boolean).length;

  const featureForNavItem = {
    sso: 'sso',
    developer: 'api',
    integrations: 'integrations',
    'audit-log': 'auditLog',
  };

  const isNavItemVisible = item => {
    const featureKey = featureForNavItem[item.key];
    if (!featureKey) {
      return true;
    }
    return features[featureKey];
  };

  // Organized navigation structure with sections
  const navigationSections = [
    {
      title: 'organization',
      items: [
        { key: 'general', label: 'generalSettings', icon: BusinessOutlined },
        { key: 'team', label: 'teamSettings', icon: GroupOutlined },
        { key: 'sso', label: 'signInSecurity', icon: SecurityOutlined },
        { key: 'billing', label: 'billingSettings', icon: CreditCardOutlined },
      ],
    },
    {
      title: 'integrations',
      items: [
        { key: 'developer', label: 'apiKeys', icon: IntegrationInstructionsOutlined },
        { key: 'mail', label: 'emailSettings', icon: EmailOutlined },
        { key: 'integrations', label: 'integrationsSettings', icon: ExtensionOutlined },
      ],
    },
    {
      title: 'personal',
      items: [
        { key: 'profile', label: 'profileSettings', icon: AdminPanelSettingsOutlined },
        { key: 'email-preferences', label: 'notifications', icon: NotificationsOutlined },
        { key: 'sessions', label: 'sessionsSettings', icon: DevicesOutlined },
      ],
    },
    {
      title: 'compliance',
      items: [
        { key: 'audit-log', label: 'auditLog', icon: HistoryOutlined },
        { key: 'delete-respondents', label: 'dataManagement', icon: DeleteForeverOutlined },
      ],
    },
  ];

  const _pages = [
    'general',
    'team',
    'sso',
    'developer',
    'mail',
    'integrations',
    'profile',
    'email-preferences',
    'sessions',
    'billing',
    'audit-log',
    'delete-respondents',
  ];

  return (
    <Layout
      returnLinkText={levels > 2 ? formatMessage(messages.back) : null}
      returnLinkTo="back"
      title={formatMessage(messages.title)}
      disableGutters
    >
      <Grid className={styles['main-grid']} container columnSpacing={2}>
        <Grid xs={12} sm={5} md={3} lg={3} id="settingsCol">
          <Sticky innerZ={1} bottomBoundary="#settingsCol" enabled>
            <List>
              {navigationSections.map((section, _sectionIndex) => (
                <React.Fragment key={section.title}>
                  <ListSubheader className={styles.listSubheader} disableSticky>
                    {formatMessage(messages[section.title])}
                  </ListSubheader>
                  {section.items.filter(isNavItemVisible).map((item, _itemIndex) => {
                    const IconComponent = item.icon;
                    return (
                      <ListItemButton
                        component={NavLink}
                        to={`/settings/${item.key}`}
                        selected={item.key === page}
                        className={styles.listItem}
                        key={item.key}
                        sx={{ gap: 1.5 }}
                      >
                        <IconComponent sx={{ fontSize: '1.25rem', color: 'text.secondary' }} />
                        {formatMessage(messages[item.label])}
                      </ListItemButton>
                    );
                  })}
                </React.Fragment>
              ))}
            </List>
          </Sticky>
        </Grid>
        <Grid xs={12} sm={7} md={9} lg={9}>
          <Outlet />
        </Grid>
      </Grid>
    </Layout>
  );
}

SettingsPage.loader = store => () => {
  const state = store.getState();
  const surveys = getSurveys(state);
  const promises = [];
  if (!isSettingsLoaded(state)) {
    promises.push(store.dispatch(fetchSettings()));
  }
  if (!surveys) {
    promises.push(store.dispatch(fetchSurveys()));
  }
  return Promise.all(promises);
};

export default SettingsPage;
